// frontend/packages/business-console/src/components/content/EditorInsightsRail.jsx
//
// The Content editor's right rail (design "Content Redesign", editor page):
// the page's publish state, its on-page SEO score + checklist, and the
// view/lead tiles. Real data only - SEO score + checklist come from the
// SitePageAudit (/page-seo) the scan produces; views/leads from page-insights.
// Joined to the open file via the heuristic content-file -> site-path map.
//
// The mock's "AI assist" card is intentionally NOT here yet: that is the
// Theme 3 AI-authoring work (Draft with AI / AI rewrite); showing dead
// buttons would violate the house honesty rule. The block-document editor
// body is a separate phase; this rail is additive to the current editor.

import React, { useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';

import { useTokens, Surface } from '../primitives';
import { contentSitePath, getAtPath } from '../../utils/contentEditorHelpers';
import { reviseContent } from '../../utils/contentApi';

// The standard on-page checks shown in the rail. Each is "pass" unless one of
// the page's real SEO issues matches its needle (issues come from the
// deterministic scorer, so this stays truthful, not invented). `fix` (when
// present) maps a FAILED check to a one-click AI copy fix through the same
// revise -> approve path the copilot uses. Structural checks (canonical,
// image alt, structured data) have no `fix`: they are not copy-fixable, so
// they stay read-only rather than showing a dead button.
const SEO_CHECKS = [
  {
    label: 'Title tag',
    needle: 'title',
    fix: {
      field: 'title',
      instruction:
        'Write a clear, specific page title of about 50 to 60 characters that fits this page. Return only the title text.',
    },
  },
  {
    label: 'Meta description',
    needle: 'meta description',
    fix: {
      field: 'description',
      instruction:
        'Write a compelling meta description of about 150 characters that summarizes this page for search results.',
    },
  },
  {
    label: 'One H1 heading',
    needle: 'h1',
    fix: {
      field: 'body',
      instruction:
        'Make sure the page body starts with exactly one H1 heading (a single line beginning with "# "). Keep all existing content and formatting otherwise.',
    },
  },
  {
    label: 'Content depth',
    needle: 'content',
    fix: {
      field: 'body',
      instruction:
        'Expand the page content so it is more substantial and useful (aim for at least 250 words), keeping the same voice, facts, and Markdown formatting. Do not invent facts.',
    },
  },
  { label: 'Canonical link', needle: 'canonical' },
  { label: 'Image alt text', needle: 'alt text' },
  { label: 'Structured data', needle: 'structured data' },
];

function Tile({ value, caption, t }) {
  return (
    <Surface radius="12px" sx={{ flex: 1, p: '10px 12px' }}>
      <Typography
        sx={{
          fontSize: 18,
          fontWeight: 900,
          letterSpacing: '-0.5px',
          color: t.text,
        }}
      >
        {value}
      </Typography>
      <Typography sx={{ fontSize: 10.5, color: t.faint }}>{caption}</Typography>
    </Surface>
  );
}

// One on-page SEO check line. When the check FAILED and is copy-fixable (and
// its target field is available), it offers a one-click "Fix with AI": it asks
// the copilot backend for a fix scoped to that field, shows the proposal, and
// applies it through the same setter the editor uses (D30: propose, then the
// operator approves). Structural checks render as a plain pass/fail line.
//
// Done-for-you fixing (the one-click apply path) is a The Ryzze tier benefit
// (`canAutoFix`). On lower tiers the same failed, copy-fixable check shows a
// quiet "Done for you on The Ryzze" nudge instead of the active button: the
// finding stays visible and honest, the upgrade path is clear, and the client
// can still fix it themselves with the copilot above.
function SeoCheckRow({ check, failed, ctx, canAutoFix, t }) {
  const [loading, setLoading] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [applied, setApplied] = useState(false);
  const [error, setError] = useState('');

  const field = check.fix?.field;
  // A check is "copy-fixable" when it failed, has a fix recipe, and its target
  // field is editable here. `fixable` (the active one-click path) additionally
  // requires the done-for-you entitlement; `copyFixable` drives the tier nudge
  // shown to lower tiers.
  const copyFixable =
    failed &&
    Boolean(check.fix) &&
    Boolean(ctx?.onApplyField && ctx?.onApplyBody) &&
    (field === 'body' || Boolean(ctx?.schemaFields?.[field]));
  const fixable = copyFixable && canAutoFix;

  async function runFix() {
    if (loading) return;
    setLoading(true);
    setError('');
    setProposal(null);
    try {
      const current =
        field === 'body'
          ? ctx.markdownBody
          : String(getAtPath(ctx.frontmatterObj, field) || '');
      const fields =
        field === 'body'
          ? { body: ctx.markdownBody }
          : { [field]: current, body: ctx.markdownBody };
      const result = await reviseContent({
        instruction: check.fix.instruction,
        fields,
      });
      const change = (result?.changes || []).find((c) => c.field === field);
      if (change) setProposal(change.value);
      else
        setError(
          "The assistant couldn't produce a fix. Try the copilot above."
        );
    } catch (e) {
      setError(e?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function apply() {
    if (proposal == null) return;
    if (field === 'body') ctx.onApplyBody(proposal);
    else ctx.onApplyField(field, proposal);
    setApplied(true);
    setProposal(null);
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875 }}>
        {failed && !applied ? (
          <ErrorOutlineRoundedIcon sx={{ fontSize: 15, color: t.amber }} />
        ) : (
          <CheckCircleRoundedIcon sx={{ fontSize: 15, color: t.green }} />
        )}
        <Typography
          sx={{
            fontSize: 12,
            color: failed && !applied ? t.text : t.textSecondary,
            fontWeight: failed && !applied ? 700 : 500,
            flex: 1,
          }}
        >
          {check.label}
        </Typography>
        {fixable && !applied ? (
          <Button
            size="small"
            variant="text"
            disabled={loading}
            onClick={runFix}
            startIcon={
              loading ? (
                <CircularProgress size={12} />
              ) : (
                <AutoAwesomeOutlinedIcon sx={{ fontSize: 14 }} />
              )
            }
            sx={{
              minWidth: 0,
              px: 0.5,
              fontSize: 11,
              color: t.purple,
              textTransform: 'none',
            }}
          >
            Fix with AI
          </Button>
        ) : copyFixable && !applied ? (
          <Typography
            title="Automatic fixes are implemented for you on the Full Service plan."
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              color: t.faint,
              whiteSpace: 'nowrap',
            }}
          >
            Done for you on Full Service
          </Typography>
        ) : null}
      </Box>

      {error ? (
        <Typography sx={{ fontSize: 11, color: t.red, ml: 2.75, mt: 0.25 }}>
          {error}
        </Typography>
      ) : null}

      {proposal != null ? (
        <Box
          sx={{
            ml: 2.75,
            mt: 0.5,
            p: 1,
            borderRadius: '8px',
            bgcolor: t.subtle,
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              color: t.textSecondary,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              mb: 0.75,
            }}
          >
            {String(proposal).slice(0, 220)}
            {String(proposal).length > 220 ? '...' : ''}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            <Button size="small" variant="contained" onClick={apply}>
              Apply
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => setProposal(null)}
              sx={{ textTransform: 'none' }}
            >
              Dismiss
            </Button>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}

export default function EditorInsightsRail({
  file,
  metricsByPath,
  previewUrls,
  publishStatus,
  schemaFields,
  frontmatterObj,
  markdownBody,
  canAutoFix = false,
  onApplyField,
  onApplyBody,
}) {
  const t = useTokens();
  if (!file) return null;

  const path = contentSitePath(file);
  const m = (metricsByPath && metricsByPath[path]) || {};
  const issues = Array.isArray(m.issues) ? m.issues : [];
  const hasSeo = typeof m.onPageScore === 'number';
  const score = hasSeo ? m.onPageScore : null;
  const scoreColor =
    score == null
      ? t.faint
      : score >= 80
        ? t.green
        : score >= 50
          ? t.amber
          : t.red;

  const modified = Boolean(file?.modifiedSincePublish);
  const aheadBy = Number(publishStatus?.ahead_by ?? 0);
  const prodUrl = previewUrls?.prodUrl || null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Publish state */}
      <Surface sx={{ p: '14px 16px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: modified ? t.amber : t.green,
            }}
          />
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: t.text }}>
            {modified ? 'Unpublished changes' : 'Published'}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 11.5, color: t.muted, mt: 0.75 }}>
          {prodUrl ? (
            <>
              Live at{' '}
              <Box
                component="span"
                sx={{ color: t.textSecondary, fontWeight: 600 }}
              >
                {prodUrl.replace(/^https?:\/\//, '')}
                {path === '/' ? '' : path}
              </Box>
              {'. '}
            </>
          ) : null}
          {aheadBy > 0
            ? `${aheadBy} change${aheadBy === 1 ? '' : 's'} waiting to publish.`
            : 'Edits go live when you publish.'}
        </Typography>
      </Surface>

      {/* On-page SEO */}
      <Surface sx={{ p: '14px 16px' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '.6px',
              textTransform: 'uppercase',
              color: t.faint,
              flex: 1,
            }}
          >
            On-page SEO
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 900, color: scoreColor }}>
            {score == null ? '--' : `${score}`}
            <Box
              component="span"
              sx={{ fontSize: 11, color: t.faint, fontWeight: 700 }}
            >
              {' '}
              / 100
            </Box>
          </Typography>
        </Box>

        {hasSeo ? (
          <>
            <Box
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: t.subtle,
                overflow: 'hidden',
                mb: 1.25,
              }}
            >
              <Box
                sx={{ width: `${score}%`, height: '100%', bgcolor: scoreColor }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.625 }}>
              {SEO_CHECKS.map((c) => {
                const failed = issues.some((i) =>
                  String(i).toLowerCase().includes(c.needle)
                );
                return (
                  <SeoCheckRow
                    key={c.label}
                    check={c}
                    failed={failed}
                    canAutoFix={canAutoFix}
                    ctx={{
                      schemaFields,
                      frontmatterObj,
                      markdownBody,
                      onApplyField,
                      onApplyBody,
                    }}
                    t={t}
                  />
                );
              })}
            </Box>
          </>
        ) : (
          <Typography sx={{ fontSize: 11.5, color: t.muted }}>
            On-page SEO appears after your next site scan.
          </Typography>
        )}
      </Surface>

      {/* Performance tiles */}
      {typeof m.views === 'number' || typeof m.leads === 'number' ? (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tile
            t={t}
            value={(m.views || 0).toLocaleString()}
            caption="views · 30d"
          />
          <Tile t={t} value={m.leads || 0} caption="leads" />
        </Box>
      ) : null}
    </Box>
  );
}
